import { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Compact page chrome for data-heavy list pages (Poligoane, Baza de date).
 *
 * These pages live on laptops with ~700 px of usable height, so the
 * title / stats / filter block has to stay under ~2 short rows — every
 * pixel spent here is a table row the user can't see. The old layout
 * (big title + subtitle, a grid of stat cards, an always-open filter
 * card) ate ~300 px before the first row of data.
 */

export function PageToolbar({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-1.5', className)}
      {...rest}
    />
  );
}

/** Page title. Small by design — the header breadcrumb already names the
 *  page; `hint` carries the old subtitle as a tooltip instead of a line. */
export function PageTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <h1
      title={hint}
      className="truncate text-base font-semibold text-slate-900 dark:text-slate-100"
    >
      {title}
    </h1>
  );
}

/** Right-aligned action cluster (buttons). */
export function PageActions({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ml-auto flex flex-wrap items-center gap-1.5', className)} {...rest} />;
}

export type StatColor = 'emerald' | 'sky' | 'amber' | 'violet';

export interface StatItem {
  label: string;
  value: number | string;
  icon?: React.ElementType;
  color?: StatColor;
}

const statIconColor: Record<StatColor, string> = {
  emerald: 'text-emerald-600 dark:text-emerald-400',
  sky: 'text-sky-600 dark:text-sky-400',
  amber: 'text-amber-600 dark:text-amber-400',
  violet: 'text-violet-600 dark:text-violet-400',
};

/**
 * One-line stat readout replacing the old 3–4 card grid (~56 px -> ~20 px).
 * Values stay prominent; labels shrink and drop out on narrow screens.
 */
export function StatStrip({ items, className }: { items: StatItem[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <div
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-xs sm:gap-x-3',
        className,
      )}
    >
      {items.map((s, i) => {
        const Icon = s.icon;
        const color = s.color ?? 'emerald';
        return (
          <span
            key={`${s.label}-${i}`}
            className="inline-flex items-center gap-1 whitespace-nowrap"
            title={`${s.label}: ${s.value}`}
          >
            {Icon ? <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', statIconColor[color])} /> : null}
            <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {s.value}
            </span>
            {/* Hidden visually on phones, still read out by screen readers —
                a bare number with no label is meaningless. */}
            <span className="sr-only text-slate-500 sm:not-sr-only sm:inline dark:text-slate-400">
              {s.label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** Default id linking `FilterToggleButton` to `FilterPanel`. Pages that
 *  render more than one panel must pass their own. */
export const FILTER_PANEL_ID = 'filter-panel';

/**
 * Toggle for the collapsible filter panel. Carries the active-filter
 * count so a collapsed panel never hides the fact that a filter is on.
 */
export function FilterToggleButton({
  open,
  count,
  onClick,
  label,
  controls = FILTER_PANEL_ID,
  className,
  id,
}: {
  open: boolean;
  count: number;
  onClick: () => void;
  label: string;
  /** id of the panel this button expands. */
  controls?: string;
  className?: string;
  /** Lets a close control inside the panel hand focus back here. */
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      aria-expanded={open}
      // Only while the panel is mounted — callers render it conditionally,
      // and a dangling aria-controls target is worse than none.
      aria-controls={open ? controls : undefined}
      // The visible label is hidden below `sm`, and the badge is the only
      // signal that a collapsed panel holds active filters — fold the
      // count into the accessible name so it is never silent.
      aria-label={count > 0 ? `${label} (${count})` : label}
      className={cn(
        'inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50',
        'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
        className,
      )}
    >
      <Filter className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
      {count > 0 ? (
        <span className="rounded-full bg-forest-600 px-1.5 text-xs font-semibold tabular-nums text-white">
          {count}
        </span>
      ) : null}
      {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * Open/closed state for a filter panel, remembered per page in
 * localStorage so a user who wants the filters pinned open only says so
 * once. Defaults to closed — the space belongs to the table.
 */
export function useFilterPanel(storageKey: string, defaultOpen = false) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw === null ? defaultOpen : raw === '1';
    } catch {
      return defaultOpen;
    }
  });

  const toggle = useCallback(() => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* private mode / quota — the in-memory state still works */
    }
  }, [open, storageKey]);

  return { open, toggle };
}

/** Grid wrapper for the filter controls themselves. `cn()` has no
 *  tailwind-merge, so a `className` column override only takes effect
 *  upward (a larger `grid-cols-N` wins by stylesheet order). */
export function FilterPanel({
  className,
  id = FILTER_PANEL_ID,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      id={id}
      className={cn(
        'grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
        className,
      )}
      {...rest}
    />
  );
}

/** Compact checkbox used inside the filter panel. */
export function FilterCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      title={label}
      className="flex h-8 items-center gap-2 rounded-md border border-transparent px-1 text-sm text-slate-600 dark:text-slate-300"
    >
      <input
        type="checkbox"
        className="h-4 w-4 flex-shrink-0 rounded border-slate-300 text-forest-600 focus:ring-forest-500"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="truncate">{label}</span>
    </label>
  );
}
